if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.addEventListener('keydown', (event) => this.onDialogBoundaryKeydown(event));
    }

    connectedCallback() {
      const collection = this.closest("#ProductGridContainer");
      if (collection) this.dataset.otfCollectionOwner = collection.id;
      super.connectedCallback();
    }

    show(opener) {
      if (opener.getAttribute('aria-disabled') === 'true') return;
      rememberQuickBuySelection(this);
      const context = quickBuySelectionContext(this, opener);
      const purchaseMode = opener.dataset.otfPurchaseMode === 'size' ? 'size' : 'shop';
      const liveForm = this.modalContent.querySelector('product-form');
      const sameContext = this.otfSelectionContext?.owner === context.owner && this.otfSelectionContext?.key === context.key;
      const liveUrl = liveForm?.querySelector('.variant--selection-options')?.dataset.url;
      if (!this.classList.contains('hide-popup') && liveForm && sameContext && liveUrl && new URL(liveUrl, window.location.origin).pathname === context.productPath && (purchaseMode !== 'size' || quickBuyMatchesPicturedColor(quickBuyOptionSnapshot(liveForm), context))) {
        this.openedBy = opener;
        this.dataset.otfPurchaseMode = opener.dataset.otfPurchaseMode === 'size' ? 'size' : 'shop';
        updateQuickBuyPresentation(this);
        trapFocus(this, quickBuyFocusTarget(this));
        return;
      }
      this.otfSelectionContext = context;
      this.otfSelectionStates ||= new WeakMap();
      const priorSelection = this.otfSelectionStates.get(context.owner)?.get(context.key) || null;
      const savedSelection = purchaseMode === 'size' && priorSelection && !quickBuyMatchesPicturedColor(priorSelection.options, context) ? null : priorSelection;
      this.openedBy = opener;
      this.cancelRequests();
      this.modalContent.setAttribute('inert', '');
      this.dataset.otfPurchaseMode = opener.dataset.otfPurchaseMode === 'size' ? 'size' : 'shop';
      const controller = new AbortController();
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 12000);
      const clearLoadTimeout = () => clearTimeout(timeout);
      controller.signal.addEventListener('abort', clearLoadTimeout, { once: true });
      this.loadController = controller;
      this.loadIntent = quickAddOverlayIntent.claim(this);
      const intent = this.loadIntent;
      const previousPopup = document.body.getAttribute('active-popup');
      const current = () => this.ownsInitialLoad(controller, intent);
      let showing = false;
      const ownsPopup = () => {
        const activePopup = document.body.getAttribute('active-popup');
        return activePopup === previousPopup || (showing && ['product-model', 'quick-add-modal'].includes(activePopup));
      };
      this.loadingOpener = opener;
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('btn--loading');
      var productUrl = opener.getAttribute('data-product-url');
      const sectionUrl = new URL(productUrl, window.location.origin);
      sectionUrl.searchParams.delete('variant');
      sectionUrl.searchParams.delete('option_values');
      sectionUrl.searchParams.set('section_id', 'product-quick-buy');
      if (savedSelection) sectionUrl.searchParams.set('option_values', savedSelection.options.map(option => option.id).filter(Boolean).join(','));
      const cardColorId = sectionUrl.searchParams.get('otf_color') || cardCheckedColorId(opener);
      const useResponse = (responseText) => {
          if (!current() || !ownsPopup()) return;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector('div[id^="product-quick-buy"]');
          this.productElement = productElement;
          if (!this.productElement) throw new Error('Quick buy markup is missing.');
          if (this.productElement.hasAttribute('data-otf-quick-buy-page-only')) {
            window.location.assign(productUrl);
            return;
          }
          if (this.productElement.querySelector('[name="id"][data-requires-selling-plan="true"]')) {
            window.location.assign(productUrl);
            return;
          }
          
          window.OTFSectionStyles?.retain(responseHTML);
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.modalContent.removeAttribute('inert');
          if (!current() || !ownsPopup()) return;
          const form = this.modalContent.querySelector('product-form');
          if (!form) throw new Error('Quick buy form is missing.');
          form.otfVariantPending = false;
          const restored = restoreQuickBuySelection(form, savedSelection);
          updateQuickBuyVariantInput($(form), responseHTML, quickBuySelectedValues(form));
          updateQuickBuyButton($(form), responseHTML);
          updateQuickBuyProductLink(form);
          updateQuickBuyPresentation(this);
          this._otfThumbColor = null;
          updateQuickBuyThumbnail($(this), responseHTML, this.modalContent.querySelector('input[name="id"]'), quickBuyColorValueId(this.modalContent));

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          if (!current() || !ownsPopup()) return;
          showing = true;
          super.show(opener);
          if (!current() || !ownsPopup()) return;
          $(document).find("body").attr("active-popup", 'quick-add-modal');
          $(document).find('.theme-popup-overlay').removeClass('hidden-overlay');
          if (!restored) this.selectCardColor(cardColorId, opener.dataset.otfCardColor);
          updateQuickBuyPresentation(this);
          this.refocusDialogWhenVisible();
      };
      fetch(sectionUrl.toString(), { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Quick buy could not be loaded.');
          return response.text();
        })
        .then(responseText => useResponse(responseText))
        .catch((error) => {
          const ownsLoad = controller === this.loadController && this.isConnected && quickAddOverlayIntent.current === intent;
          if ((timedOut ? ownsLoad : error.name !== 'AbortError' && current()) && ownsPopup()) window.location.assign(productUrl);
        })
        .finally(() => {
          clearLoadTimeout();
          controller.signal.removeEventListener('abort', clearLoadTimeout);
          if (controller === this.loadController) this.releaseLoadingOpener();
        });
    }

    // A colour chosen on the product card arrives as otf_color (its option-value ID). It is selected through
    // the same change path as a manual pick, so the size stays unchosen. A size option can never be matched.
    selectCardColor(colorId, colorName) {
      if ((!colorId || !/^\d+$/.test(colorId)) && !colorName) return;
      const colorOptions = Array.from(this.modalContent.querySelectorAll('.js-option-select[data-otf-color] option[data-option-value-id]'));
      const option = colorOptions.find(candidate => candidate.dataset.optionValueId === colorId)
        || colorOptions.find(candidate => candidate.value === colorName);
      const select = option?.closest('select');
      if (!select || select.value === option.value) return;
      select.value = option.value;
      select.dataset.otfColorHandoff = 'true';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      delete select.dataset.otfColorHandoff;
    }

    refocusDialogWhenVisible() {
      this.cancelInitialFocus();
      const controller = this.loadController;
      const intent = this.loadIntent;
      const dialog = this.querySelector('[role="dialog"]');
      if (!dialog) return;
      // Quick add opens the same dialog and lands on the first option still to be chosen (the size placeholder),
      // or on Add to cart when every option is already resolved; the color cue lands on the color select.
      const unresolvedTarget = () => {
        const selects = Array.from(this.modalContent.querySelectorAll('.js-option-select:not([hidden])'));
        const unresolved = selects.find(select => !select.value);
        if (unresolved) return unresolved;
        const add = this.modalContent.querySelector('.quick-add__submit[name="add"]');
        if (add && !add.disabled && add.getAttribute('aria-disabled') !== 'true') return add;
        return selects[0] || dialog;
      };
      const preferredTarget = () => this.openedBy?.hasAttribute('data-otf-focus-unresolved')
        ? unresolvedTarget()
        : this.openedBy?.hasAttribute('data-otf-open-colors')
          ? this.modalContent.querySelector('.js-option-select[data-otf-color]:not([hidden])') || dialog
          : dialog;
      const modeTarget = () => this.dataset.otfPurchaseMode === 'size' ? quickBuyFocusTarget(this) : preferredTarget();
      const generation = this.initialFocusGeneration;
      const openingURL = window.location.href;
      const deadline = performance.now() + 750;
      let trapHandler = trapFocusHandlers.focusin;
      const boundary = this.dialogBoundary = { controller, intent, dialog, trapHandler };
      const stop = () => {
        if (generation === this.initialFocusGeneration) this.cancelInitialFocus();
      };
      const anotherPopup = () => Array.from(document.querySelectorAll('.rt-theme-popup:not(.hide-popup), .overlay-navigation-panel__popup:not(.hide-popup)')).some(popup => popup !== this);
      const current = () => generation === this.initialFocusGeneration && this.ownsInitialLoad(controller, intent);
      this.initialFocusListener = (event) => {
        if (generation !== this.initialFocusGeneration) return;
        if (this.contains(event.target) || (event.target !== document.body && event.target !== this.openedBy)) stop();
      };
      document.addEventListener('focusin', this.initialFocusListener, true);
      const refocus = () => {
        if (generation !== this.initialFocusGeneration) return;
        this.initialFocusFrame = null;
        if (!current() || document.hidden || !document.hasFocus() || performance.now() >= deadline || this.classList.contains('hide-popup') || dialog.closest('[hidden], [inert], [aria-hidden="true"]') || !dialog.isConnected || !this.contains(dialog) || window.location.href !== openingURL) return stop();
        if (document.body.getAttribute('active-popup') !== 'quick-add-modal' || anotherPopup() || !trapHandler || trapHandler !== trapFocusHandlers.focusin) return stop();
        const active = document.activeElement;
        if ((this.contains(active) && (active !== dialog || modeTarget() === dialog)) || (!this.contains(active) && active !== document.body && active !== this.openedBy)) return stop();
        const modalStyle = getComputedStyle(this);
        const dialogStyle = getComputedStyle(dialog);
        if (this.getClientRects().length && dialog.getClientRects().length && modalStyle.visibility === 'visible' && modalStyle.display !== 'none' && dialogStyle.visibility === 'visible' && dialogStyle.display !== 'none') {
          // Rebuild only our trap after rendering, capturing it before focus can hand off ownership.
          trapFocus(this, {
            getClientRects: () => dialog.getClientRects(),
            focus: () => {
              trapHandler = trapFocusHandlers.focusin;
              boundary.trapHandler = trapHandler;
              modeTarget().focus({ preventScroll: true });
            }
          });
          if (!current() || document.body.getAttribute('active-popup') !== 'quick-add-modal' || anotherPopup() || trapHandler !== trapFocusHandlers.focusin) return stop();
          if (this.contains(document.activeElement)) return stop();
        }
        this.initialFocusFrame = requestAnimationFrame(refocus);
      };
      this.initialFocusFrame = requestAnimationFrame(refocus);
    }

    // The labeled inner root precedes the controls; visible Close stays in the outer trap.
    onDialogBoundaryKeydown(event) {
      if (event.defaultPrevented || event.code?.toUpperCase() !== 'TAB' || !event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
      const boundary = this.dialogBoundary;
      if (!boundary || !boundary.controller || !boundary.trapHandler || !this.ownsInitialLoad(boundary.controller, boundary.intent) || boundary.trapHandler !== trapFocusHandlers.focusin) return;
      const dialog = boundary.dialog;
      if (event.target !== dialog || document.activeElement !== dialog || dialog !== this.querySelector('[role="dialog"]') || document.hidden || !document.hasFocus()) return;
      if (this.classList.contains('hide-popup') || document.body.getAttribute('active-popup') !== 'quick-add-modal' || dialog.closest('[hidden], [inert], [aria-hidden="true"]')) return;
      if (Array.from(document.querySelectorAll('.rt-theme-popup:not(.hide-popup), .overlay-navigation-panel__popup:not(.hide-popup)')).some(popup => popup !== this)) return;
      const usable = (element) => {
        const style = getComputedStyle(element);
        return element.getClientRects().length && !element.closest('[hidden], [inert], [aria-hidden="true"]') && style.visibility === 'visible' && style.display !== 'none';
      };
      if (!usable(this) || !usable(dialog)) return;
      const last = getFocusableElements(this).filter(usable).pop();
      event.preventDefault();
      last?.focus({ preventScroll: true });
    }

    hide() {
      const originalOpener = this.openedBy;
      rememberQuickBuySelection(this);
      this.cancelRequests();
      this.classList.add('hide-popup');
      if (document.body.getAttribute('active-popup') === 'quick-add-modal') {
        document.body.removeAttribute('active-popup');
        document.querySelector('.theme-popup-overlay')?.classList.add('hidden-overlay');
      }
      if (originalOpener && (!originalOpener.isConnected || originalOpener.closest('[hidden], [inert], [aria-hidden="true"]') || !originalOpener.getClientRects().length)) {
        this.openedBy = document.querySelector('#cart-icon-bubble') || originalOpener.closest('[data-otf-card]')?.querySelector('[id^="CardLink-"]') || originalOpener;
      }
      super.hide();
      this.openedBy = originalOpener;
      // Desktop hover controls can be hidden after closing; keep category focus on the same product.
      const categoryCard = this.openedBy?.closest('[data-otf-card]');
      if (categoryCard && this.openedBy.isConnected &&
          (document.activeElement === document.body || this.contains(document.activeElement))) {
        categoryCard.querySelector('[id^="CardLink-"]')?.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      this.cancelRequests();
    }

    ownsInitialLoad(controller, intent) {
      return controller === this.loadController && !controller.signal.aborted && this.isConnected && quickAddOverlayIntent.current === intent;
    }

    releaseLoadingOpener() {
      this.loadingOpener?.removeAttribute('aria-disabled');
      this.loadingOpener?.classList.remove('btn--loading');
      this.loadingOpener = null;
    }

    cancelInitialFocus() {
      this.initialFocusGeneration = (this.initialFocusGeneration || 0) + 1;
      document.removeEventListener('focusin', this.initialFocusListener, true);
      this.initialFocusListener = null;
      if (this.initialFocusFrame != null) cancelAnimationFrame(this.initialFocusFrame);
      this.initialFocusFrame = null;
    }

    cancelInitialLoad() {
      this.dialogBoundary = null;
      this.loadController?.abort();
      this.loadController = null;
      this.releaseLoadingOpener();
      this.cancelInitialFocus();
    }

    cancelRequests() {
      this.cancelInitialLoad();
      this.querySelectorAll('product-form').forEach(form => {
        form.otfVariantPending = true;
        lockQuickBuyPurchase(form);
        setQuickBuyPayment(form, false);
        form.otfVariantController?.abort();
        form.otfVariantRequest = null;
        form.otfFailedVariantRequest = null;
        form.otfPaymentObserver?.disconnect();
        form.otfPaymentObserver = null;
        const retryButton = form.querySelector('[data-otf-quick-buy-retry]');
        if (retryButton) retryButton.hidden = true;
      });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  });
  $(document).on('change','.js-option-select',function(){
    if (!this.closest('quick-add-modal')) return;
    var closeForm = $(this).closest('product-form');
    var variantContainer = closeForm.find('.variant--selection-options');
    var currentProductUrl = variantContainer.attr('data-url');
    var sectionId = variantContainer.attr('data-section');

    var selectedValueIds = [];
    const selectedValues = new Map();
    var newProductUrl = $(this).find('option:selected').attr('data-product-url') || null;

    closeForm.find('.js-option-select').each(function(){
      selectedValues.set(this.dataset.index, this.value);
      var selectedOption = $(this).find('option:selected');
      var optionValueId = selectedOption.attr('data-option-value-id');

      if (optionValueId) {
        selectedValueIds.push(optionValueId);
      }


    });

    var productUrl = newProductUrl || currentProductUrl;
    const formElement = closeForm[0];
    if (this.dataset.otfColorHandoff !== 'true') formElement.otfDeliberateSelection = true;
    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.delete('variant');
    requestUrl.searchParams.set('section_id', sectionId);
    requestUrl.searchParams.set('option_values', selectedValueIds.join(','));
    const request = {
      productUrl,
      requestUrl: requestUrl.toString(),
      shouldSwapProduct: currentProductUrl !== productUrl,
      selectedValues,
      focusIndex: this.dataset.index,
      colorChanged: this.hasAttribute('data-otf-color'),
    };
    formElement.otfVariantRequest = request;
    if (request.colorChanged) {
      const modal = formElement.closest('quick-add-modal');
      updateQuickBuyPrice($(modal), modal);
      updateQuickBuyThumbnail($(modal), modal, formElement.querySelector('input[name="id"]'), quickBuyColorValueId(formElement));
    }
    rememberQuickBuySelection(formElement.closest('quick-add-modal'));
    updateQuickBuyGridSelection(formElement);
    renderQuickBuyVariant(formElement, request);
  });

  $(document).on('click', 'quick-add-modal [data-otf-size-value]', function () {
    if (this.disabled) return;
    const form = this.closest('product-form');
    const select = Array.from(form?.querySelectorAll('.js-option-select[data-otf-size]') || []).find(control => control.dataset.index === this.dataset.otfIndex);
    const option = Array.from(select?.options || []).find(value => value.value === this.dataset.otfSizeValue);
    if (!option || option.disabled || option.dataset.otfAvailable === 'false') return;
    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function quickBuySelectedValues(form) {
    return new Map(Array.from(form.querySelectorAll('.js-option-select')).map(select => [select.dataset.index, select.value]));
  }

  function quickBuySelectionContext(modal, opener) {
    const url = new URL(opener.getAttribute('data-product-url'), window.location.origin);
    const colorId = url.searchParams.get('otf_color') || cardCheckedColorId(opener) || '';
    const colorName = opener.dataset.otfCardColor || '';
    const color = colorId || colorName;
    const owner = opener.closest('[data-otf-card]') || modal;
    return { owner, productPath: url.pathname, colorId, colorName, key: `${url.pathname}|${color}` };
  }

  function quickBuyOptionSnapshot(form, preserveSize = true) {
    return Array.from(form.querySelectorAll('.js-option-select')).map(select => {
      const resetSize = !preserveSize && select.hasAttribute('data-otf-size');
      return { index: select.dataset.index, name: select.getAttribute('aria-label'), id: resetSize ? '' : select.selectedOptions[0]?.dataset.optionValueId || '', value: resetSize ? '' : select.value };
    });
  }

  function quickBuyMatchesPicturedColor(options, context) {
    const color = options.find(option => /^colou?r$/i.test((option.name || '').trim()));
    return !color || (context.colorId ? color.id === context.colorId : Boolean(context.colorName) && color.value === context.colorName);
  }

  function rememberQuickBuySelection(modal) {
    const form = modal?.modalContent.querySelector('product-form');
    const context = modal?.otfSelectionContext;
    if (!form?.otfDeliberateSelection || !context) return;
    const url = form.querySelector('.variant--selection-options')?.dataset.url;
    if (!url || new URL(url, window.location.origin).pathname !== context.productPath) return;
    // Reopening keeps the shopper's colour/context, but a new purchase needs a
    // fresh size choice. Active, already-open dialogs retain their live values.
    const options = quickBuyOptionSnapshot(form, false);
    modal.otfSelectionStates ||= new WeakMap();
    const states = modal.otfSelectionStates.get(context.owner) || new Map();
    states.set(context.key, { options });
    modal.otfSelectionStates.set(context.owner, states);
  }

  function restoreQuickBuySelection(form, saved) {
    if (!saved) return false;
    const selects = Array.from(form.querySelectorAll('.js-option-select'));
    const complete = selects.length === saved.options.length && selects.every(select => {
      const prior = saved.options.find(option => option.index === select.dataset.index && option.name === select.getAttribute('aria-label'));
      return prior && (!prior.id && !prior.value || Array.from(select.options).some(option => option.dataset.optionValueId === prior.id));
    });
    selects.forEach(select => {
      const prior = complete && saved.options.find(option => option.index === select.dataset.index);
      const option = prior?.id && Array.from(select.options).find(candidate => candidate.dataset.optionValueId === prior.id);
      if (select.hasAttribute('data-otf-size')) select.value = '';
      else if (option) select.value = option.value;
    });
    form.otfDeliberateSelection = complete && !selects.some(select => select.hasAttribute('data-otf-size'));
    return complete;
  }

  function setQuickBuyPayment(form, eligible) {
    form.querySelectorAll('[data-otf-accelerated]').forEach(container => {
      if (container.hidden !== !eligible) container.hidden = !eligible;
      container.toggleAttribute('inert', !eligible);
      container.setAttribute('aria-hidden', String(!eligible));
    });
  }

  function updateQuickBuyPayment(form) {
    const modal = form.closest('quick-add-modal');
    const input = form.querySelector('input[name="id"]');
    const add = form.querySelector('[data-otf-quick-buy-add],.quick-add__submit[name="add"]');
    const values = Array.from(form.querySelectorAll('.js-option-select')).map(select => select.value);
    let options = null;
    try { options = JSON.parse(input?.dataset.otfVariantOptions || 'null'); } catch (error) { options = null; }
    const exact = Array.isArray(options) && options.length === values.length && options.every((value, index) => value === values[index]);
    const eligible = modal?.dataset.otfPurchaseMode === 'shop' && !modal.classList.contains('hide-popup') && !form.otfVariantPending && !form.otfFailedVariantRequest && !form.error && input && !input.disabled && input.value && input.value === input.dataset.otfVariantId && input.dataset.otfAvailable === 'true' && exact && values.every(Boolean) && add && !add.disabled && add.getAttribute('aria-disabled') !== 'true' && !add.classList.contains('btn--loading');
    setQuickBuyPayment(form, Boolean(eligible));
  }

  function quickBuyModeHidden(element, hidden) {
    if (!element) return;
    if (!element.otfModeOriginal) element.otfModeOriginal = { hidden: element.hidden, inert: element.hasAttribute('inert') };
    element.hidden = hidden || element.otfModeOriginal.hidden;
    element.toggleAttribute('inert', hidden || element.otfModeOriginal.inert);
  }

  function updateQuickBuyGridSelection(form) {
    form.querySelectorAll('[data-otf-size-value]').forEach(button => {
      const select = Array.from(form.querySelectorAll('.js-option-select[data-otf-size]')).find(control => control.dataset.index === button.dataset.otfIndex);
      button.setAttribute('aria-pressed', String(select?.value === button.dataset.otfSizeValue));
    });
  }

  function quickBuyFocusTarget(modal, index, value) {
    if (modal.dataset.otfPurchaseMode === 'size') {
      const buttons = Array.from(modal.querySelectorAll('[data-otf-size-value]:not(:disabled)'));
      return buttons.find(button => button.dataset.otfIndex === index && button.dataset.otfSizeValue === value) || buttons.find(button => button.getAttribute('aria-pressed') === 'true') || buttons[0] || modal.querySelector('[data-otf-quick-buy-add]:not(:disabled)') || modal.querySelector('[role="dialog"]');
    }
    return Array.from(modal.querySelectorAll('.js-option-select:not([hidden])')).find(select => select.dataset.index === index) || modal.querySelector('[role="dialog"]');
  }

  function updateQuickBuyPresentation(modal) {
    const form = modal.modalContent.querySelector('product-form');
    if (!form) return;
    const controls = Array.from(form.querySelectorAll('.js-option-select'));
    const safeSizeMode = controls.filter(select => select.hasAttribute('data-otf-size')).length === 1 && controls.every(select => select.hasAttribute('data-otf-size') || select.hasAttribute('data-otf-color') || Array.from(select.options).filter(option => option.value).length <= 1);
    if (modal.dataset.otfPurchaseMode === 'size' && !safeSizeMode) modal.dataset.otfPurchaseMode = 'shop';
    const sizeMode = modal.dataset.otfPurchaseMode === 'size';
    const suitGuidance = form.querySelector('.variant--selection-options > [data-otf-quick-buy-suit-guidance]');
    const sourceSuitPolicy = suitGuidance?.querySelector('[data-otf-quick-buy-suit-policy]');
    const actions = form.querySelector('.otf-quick-buy-actions');
    const currentSuitPolicy = actions?.querySelector(':scope > [data-otf-quick-buy-suit-policy]');
    if (sourceSuitPolicy && actions) {
      currentSuitPolicy?.remove();
      actions.prepend(sourceSuitPolicy);
    } else if (!suitGuidance) {
      currentSuitPolicy?.remove();
    }
    modal.querySelectorAll('[data-otf-quick-shop-title]').forEach(title => { title.hidden = sizeMode; });
    modal.querySelectorAll('[data-otf-size-picker-title]').forEach(title => { title.hidden = !sizeMode; });
    form.querySelectorAll('.js-option-select').forEach(select => {
      const field = select.closest('fieldset');
      const isSize = select.hasAttribute('data-otf-size');
      quickBuyModeHidden(field, sizeMode && !isSize);
      if (!isSize) return;
      let grid = field?.querySelector('[data-otf-size-grid]');
      if (!grid && field) {
        grid = document.createElement('div');
        grid.className = 'otf-size-grid';
        grid.dataset.otfSizeGrid = '';
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', select.getAttribute('aria-label') || '');
        Array.from(select.options).filter(option => option.value && option.dataset.optionValueId).forEach(option => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.otfSizeValue = option.value;
          button.dataset.otfIndex = select.dataset.index;
          button.textContent = option.value;
          button.setAttribute('aria-label', option.textContent);
          button.disabled = option.disabled || option.dataset.otfAvailable === 'false';
          grid.append(button);
        });
        field.append(grid);
      }
      if (grid) grid.hidden = !sizeMode;
      quickBuyModeHidden(select.closest('.select') || select, sizeMode);
    });
    modal.querySelectorAll('.otf-quick-buy-guidance').forEach(element => quickBuyModeHidden(element, false));
    modal.querySelectorAll('.otf-quick-buy-links').forEach(element => quickBuyModeHidden(element, sizeMode));
    const color = form.querySelector('.js-option-select[data-otf-color]')?.value || '';
    modal.querySelectorAll('[data-otf-size-color-summary]').forEach(summary => { summary.textContent = color; summary.hidden = !sizeMode || !color; });
    updateQuickBuyGridSelection(form);
    if (!form.otfPaymentObserver) {
      const observer = new MutationObserver(() => updateQuickBuyPayment(form));
      const add = form.querySelector('[data-otf-quick-buy-add],.quick-add__submit[name="add"]');
      const error = form.querySelector('.product-form__error-message-wrapper');
      if (add) observer.observe(add, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      if (error) observer.observe(error, { attributes: true, attributeFilter: ['hidden'] });
      form.otfPaymentObserver = observer;
    }
    updateQuickBuyPayment(form);
  }

  function syncQuickBuyCardColor(modal) {
    const card = modal.openedBy?.closest('[data-otf-card].product-grid-item');
    const form = modal.modalContent.querySelector('product-form');
    const color = form?.querySelector('.js-option-select[data-otf-color]')?.selectedOptions[0];
    const url = form?.querySelector('.variant--selection-options')?.dataset.url;
    if (!card || !color || !url || new URL(url, window.location.origin).pathname !== modal.otfSelectionContext?.productPath) return;
    const swatch = Array.from(card.querySelectorAll('.radio-card-swatch:not([data-product-url])')).find(input => input.dataset.otfColorId === color.dataset.optionValueId);
    let media = null;
    try { media = JSON.parse(swatch?.dataset.variantMedia || 'null'); } catch (error) { media = null; }
    if (!swatch || swatch.checked || !media?.preview_image?.src) return;
    swatch.checked = true;
    swatch.dispatchEvent(new Event('change', { bubbles: true }));
    modal.otfSelectionStates?.get(modal.otfSelectionContext.owner)?.delete(modal.otfSelectionContext.key);
    modal.otfSelectionContext = quickBuySelectionContext(modal, modal.openedBy);
  }

  function updateQuickBuyProductLink(form) {
    const modal = form.closest('quick-add-modal');
    const url = form.querySelector('.variant--selection-options')?.dataset.url;
    if (!modal || !url) return;
    const link = new URL(url, window.location.origin);
    link.searchParams.delete('variant');
    link.searchParams.delete('otf_color');
    link.searchParams.delete('option_values');
    const input = form.querySelector('input[name="id"]');
    const color = quickBuyColorValueId(form);
    if (input?.value && !input.disabled) link.searchParams.set('variant', input.value);
    else if (color) link.searchParams.set('otf_color', color);
    modal.querySelectorAll('.product--title,[data-otf-quick-buy-product-link]').forEach(element => element.href = link.toString());
  }

  function cardCheckedColorId(opener) {
    const checked = opener.closest?.('[data-otf-card]')?.querySelector('.radio-card-swatch:checked');
    return checked && !checked.dataset.productUrl ? checked.dataset.otfColorId || null : null;
  }

  function currentQuickBuyRequest(form, request, controller, allowAborted = false) {
    const modal = form.closest('quick-add-modal');
    return form.isConnected && modal && !modal.classList.contains('hide-popup') &&
      request === form.otfVariantRequest && controller === form.otfVariantController && (allowAborted || !controller.signal.aborted);
  }

  function lockQuickBuyPurchase(form) {
    form.otfVariantPending = true;
    setQuickBuyPayment(form, false);
    $(form).find('.quick-add__submit[name="add"]').prop('disabled', true);
    $(form).find('input[name="id"]').val('').prop('disabled', true);
  }

  function showQuickBuyRecovery(form, request, restoreFocus) {
    lockQuickBuyPurchase(form);
    form.otfFailedVariantRequest = request;
    form.handleErrorMessage?.(form.dataset.otfSelectionError || window.cartStrings.error);
    const wrapper = form.querySelector('.product-form__error-message-wrapper');
    if (!wrapper || !form.dataset.otfSelectionRetry) return;
    let retryButton = wrapper.querySelector('[data-otf-quick-buy-retry]');
    if (!retryButton) {
      retryButton = document.createElement('button');
      retryButton.type = 'button';
      retryButton.className = 'btn btn--secondary otf-selection-retry';
      retryButton.dataset.otfQuickBuyRetry = '';
      retryButton.addEventListener('click', () => {
        const failedRequest = form.otfFailedVariantRequest;
        if (retryButton.disabled || !failedRequest || !currentQuickBuyRequest(form, failedRequest, form.otfVariantController, true)) return;
        renderQuickBuyVariant(form, failedRequest, true);
      });
      wrapper.append(retryButton);
    }
    retryButton.textContent = form.dataset.otfSelectionRetry;
    retryButton.hidden = false;
    retryButton.disabled = false;
    if (restoreFocus) retryButton.focus({ preventScroll: true });
  }

  function renderQuickBuyVariant(formElement, request, isRetry = false) {
    const closeForm = $(formElement);
    const closeModel = closeForm.closest('quick-add-modal');
    const variantContainer = closeForm.find('.variant--selection-options');
    const { productUrl, shouldSwapProduct, selectedValues } = request;
    const retryButton = formElement.querySelector('[data-otf-quick-buy-retry]');
    const restoreRetryFocus = isRetry && document.activeElement === retryButton;
    const activeSelect = document.activeElement;
    const restoreSelectFocus = formElement.contains(activeSelect) && activeSelect?.matches('.js-option-select');
    const activeChip = formElement.contains(activeSelect) && activeSelect?.matches('[data-otf-size-value]') ? activeSelect : null;
    formElement.otfVariantController?.abort();
    const controller = new AbortController();
    formElement.otfVariantController = controller;
    formElement.otfFailedVariantRequest = null;
    lockQuickBuyPurchase(formElement);
    formElement.error = false;
    if (isRetry) {
      formElement.handleErrorMessage?.(formElement.dataset.otfSelectionError || window.cartStrings.error);
      if (retryButton) {
        retryButton.disabled = true;
        retryButton.textContent = formElement.dataset.otfSelectionRetrying || formElement.dataset.otfSelectionRetry;
      }
    } else {
      if (retryButton) retryButton.hidden = true;
      formElement.handleErrorMessage?.();
    }
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 12000);
    fetch(request.requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Product options could not be loaded.');
        return response.text();
      })
      .then((responseText) => {
        if (!currentQuickBuyRequest(formElement, request, controller)) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (html.querySelector('[data-otf-quick-buy-page-only]')) {
          lockQuickBuyPurchase(formElement);
          window.location.assign(productUrl);
          return;
        }
        const sourceVariantContainer = html.querySelector('.variant--selection-options');

        if (!sourceVariantContainer) {
          throw new Error('Variant container not found in response');
        }

        const sourceInput = html.querySelector('input[name="id"]');
        if (!sourceInput) throw new Error('Variant input not found in response');
        if (!html.querySelector('.quick-add__submit[name="add"]')) throw new Error('Variant button not found in response');
        const sourceOptions = JSON.parse(sourceInput.dataset.otfVariantOptions || '[]');
        // A combination with no variant renders null (Shopify resolves no selected or first available variant): a valid
        // unavailable state, not a failed request. The purchase stays locked below because nothing matches the selection.
        if (sourceOptions !== null && !Array.isArray(sourceOptions)) throw new Error('Variant options are invalid');
        if (sourceInput.dataset.requiresSellingPlan === 'true') {
          window.location.assign(productUrl);
          return;
        }
        window.OTFSectionStyles?.retain(html);
        const deferredColorFocus = request.colorChanged && !restoreSelectFocus && formElement.contains(document.activeElement) && document.activeElement?.matches('.js-option-select[data-otf-color]') && document.activeElement.dataset.index === request.focusIndex;
        const focusStillOwned = restoreRetryFocus
          ? document.activeElement === retryButton || document.activeElement === document.body
          : restoreSelectFocus && document.activeElement === activeSelect || deferredColorFocus || activeChip && document.activeElement === activeChip;
        if (shouldSwapProduct) {
          variantContainer.attr('data-url', productUrl);
          var productTitle = html.querySelector('.product--title').textContent.trim();
          closeModel.find('.product--title').text(productTitle);
          const guidance = html.querySelector('.otf-quick-buy-guidance');
          if (guidance) closeForm.find('.otf-quick-buy-guidance').html(guidance.innerHTML);
          closeModel[0]._otfThumbColor = null;
          const payment = html.querySelector('[data-otf-accelerated]')?.cloneNode(true);
          const currentPayment = formElement.querySelector('[data-otf-accelerated]');
          if (payment) {
            payment.hidden = true;
            payment.setAttribute('inert', '');
            if (currentPayment) currentPayment.replaceWith(payment);
            else formElement.querySelector('[data-otf-quick-buy-add],.quick-add__submit[name="add"]')?.before(payment);
            if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
          } else currentPayment?.remove();
        }

        const requestedSizes = Array.from(formElement.querySelectorAll('.js-option-select[data-otf-size]'));
        const selectedSize = requestedSizes.some(select => selectedValues.get(select.dataset.index));
        const matchesSelection = Array.isArray(sourceOptions) && sourceOptions.length === selectedValues.size && sourceOptions.every((value, index) => value === selectedValues.get(String(index)));
        const resetSize = request.colorChanged && selectedSize && (!matchesSelection || sourceInput.dataset.otfAvailable !== 'true');
        if (resetSize) {
          requestedSizes.forEach(select => selectedValues.set(select.dataset.index, ''));
        }
        // A select focused after this request began (Quick add lands on the unresolved size) is re-rendered
        // with the options: keep focus on the same control instead of dropping it to the page.
        const liveSelect = document.activeElement;
        const liveIndex = !focusStillOwned && formElement.contains(liveSelect) && liveSelect?.matches('.js-option-select') ? liveSelect.dataset.index : null;
        const liveChip = !focusStillOwned && formElement.contains(liveSelect) && liveSelect?.matches('[data-otf-size-value]') ? { index: liveSelect.dataset.otfIndex, value: liveSelect.dataset.otfSizeValue } : null;
        updateQuickBuyOptions(closeForm, sourceVariantContainer, selectedValues);
        updateQuickBuyPresentation(closeModel[0]);
        if (liveIndex !== null) quickBuyFocusTarget(closeModel[0], liveIndex)?.focus({ preventScroll: true });
        if (liveChip) quickBuyFocusTarget(closeModel[0], liveChip.index, liveChip.value)?.focus({ preventScroll: true });
        if (resetSize) {
          const resetUrl = new URL(productUrl, window.location.origin);
          resetUrl.searchParams.delete('variant');
          resetUrl.searchParams.set('section_id', variantContainer.attr('data-section'));
          const optionIds = Array.from(formElement.querySelectorAll('.js-option-select')).map(select => select.selectedOptions[0]?.dataset.optionValueId).filter(Boolean);
          resetUrl.searchParams.set('option_values', optionIds.join(','));
          const nextRequest = { ...request, requestUrl: resetUrl.toString(), selectedValues: new Map(selectedValues), shouldSwapProduct: false, colorChanged: false };
          formElement.otfVariantRequest = nextRequest;
          if (focusStillOwned) quickBuyFocusTarget(closeModel[0], request.focusIndex, activeChip?.dataset.otfSizeValue)?.focus({ preventScroll: true });
          renderQuickBuyVariant(formElement, nextRequest);
          return;
        }
        updateQuickBuyPrice(closeModel, html);
        const colorValueId = quickBuyColorValueId(formElement);
        updateQuickBuyThumbnail(closeModel, html, sourceInput, colorValueId);
        updateQuickBuyVariantInput(closeForm, html, selectedValues);
        updateQuickBuyButton(closeForm, html);
        const variantInput = closeForm.find('input[name="id"]')[0];
        const productLink = new URL(productUrl, window.location.origin);
        productLink.searchParams.delete('variant');
        productLink.searchParams.delete('otf_color');
        // Until a complete variant is chosen the title link keeps the colour only, never a size.
        if (variantInput.value) productLink.searchParams.set('variant', variantInput.value);
        else if (colorValueId) productLink.searchParams.set('otf_color', colorValueId);
        closeModel.find('.product--title,[data-otf-quick-buy-product-link]').attr('href', productLink.toString());
        formElement.handleErrorMessage?.();
        if (retryButton) retryButton.hidden = true;
        formElement.otfVariantPending = false;
        syncQuickBuyCardColor(closeModel[0]);
        rememberQuickBuySelection(closeModel[0]);
        updateQuickBuyPresentation(closeModel[0]);
        if (focusStillOwned) {
          const addButton = formElement.querySelector('.quick-add__submit[name="add"]');
          const select = Array.from(formElement.querySelectorAll('.js-option-select')).find(option => option.dataset.index === request.focusIndex);
          const target = restoreRetryFocus && addButton && !addButton.disabled ? addButton : closeModel[0].dataset.otfPurchaseMode === 'size' ? quickBuyFocusTarget(closeModel[0], request.focusIndex, activeChip?.dataset.otfSizeValue) : select;
          target?.focus({ preventScroll: true });
        }
      })
      .catch((error) => {
        if ((!timedOut && error.name === 'AbortError') || !currentQuickBuyRequest(formElement, request, controller, timedOut)) return;
        console.error('Error fetching variant:', error);
        showQuickBuyRecovery(formElement, request, restoreRetryFocus && document.activeElement === retryButton);
      })
      .finally(() => clearTimeout(timeout));
  }

  function updateQuickBuyOptions(form, sourceContainer, selectedValues) {
    form.find('.variant--selection-options').html(sourceContainer.innerHTML);
    form.find('.js-option-select').each(function() {
      const value = selectedValues.get(this.dataset.index);
      if (Array.from(this.options).some(option => option.value === value)) this.value = value;
      else if (this.hasAttribute('data-otf-size')) this.value = '';
    });
  }

  function updateQuickBuyPrice(modal, html) {
    var sourcePrice = html.querySelector(`.price`);
    var currentPrice = modal.find('.price');

    if (sourcePrice && currentPrice.length) {
      currentPrice.html(sourcePrice.innerHTML);
      const sourceInput = html.querySelector('input[name="id"]');
      const colorSelect = modal[0].querySelector('.js-option-select[data-otf-color]');
      const color = colorSelect?.selectedOptions[0];
      const sourceOptions = JSON.parse(sourceInput?.dataset.otfVariantOptions || 'null');
      const matchesColor = !colorSelect || Array.isArray(sourceOptions) && sourceOptions[Number(colorSelect.dataset.index)] === colorSelect.value;
      if (!sourceInput?.dataset.otfVariantId || !matchesColor) {
        if (color?.dataset.otfPrice) {
          const sale = Boolean(color.dataset.otfComparePrice);
          currentPrice.find('[price--sale]').text(sale ? color.dataset.otfPrice : '');
          currentPrice.find('[price--regular]').toggleClass('cut--price', sale).text(sale ? color.dataset.otfComparePrice : color.dataset.otfPrice);
        } else if (sourcePrice.dataset.otfProductPrice) {
          currentPrice.find('[price--sale]').text('');
          currentPrice.find('[price--regular]').removeClass('cut--price').text(sourcePrice.dataset.otfProductPrice);
        }
      }
    }
  }

  function updateQuickBuyImage(modal, html) {
    var sourceImage = html.querySelector('.quickBuy--product-image');
    var currentImage = modal.find('.quickBuy--product-image');

    if (sourceImage?.dataset.otfImageConfirmed === 'true' && currentImage.length) {
      currentImage.attr('src', sourceImage.src);
      currentImage.attr('alt', sourceImage.alt);
      currentImage.css('visibility', '');
    } else {
      if (!sourceImage) console.warn('Source image not found in server response');
      if (!currentImage.length) console.warn('Current image not found in modal');
    }
  }

  // The option-value ID of the colour currently selected in the modal (the colour select is the non-size select labelled Color/Colour).
  function quickBuyColorValueId(root) {
    const colorSelect = root.querySelector('.js-option-select[data-otf-color]');
    return colorSelect?.selectedOptions[0]?.dataset.optionValueId || null;
  }

  // The thumbnail is kept only while it is known to depict the colour still selected. modal._otfThumbColor is the
  // option-value ID of the colour the current image was rendered for (null when unknown or concealed).
  function updateQuickBuyThumbnail(modal, html, sourceInput, colorValueId) {
    const modalElement = modal[0];
    const colorSelect = modalElement.querySelector('.js-option-select[data-otf-color]');
    const sourceOptions = JSON.parse(sourceInput.dataset.otfVariantOptions || 'null');
    const matchesColor = !colorSelect || Array.isArray(sourceOptions) && sourceOptions[Number(colorSelect.dataset.index)] === colorSelect.value;
    const sourceImage = html.querySelector('.quickBuy--product-image');
    if (sourceInput.dataset.otfVariantId && matchesColor && sourceImage?.dataset.otfImageConfirmed === 'true') {
      // A resolved variant: the section rendered that variant's image.
      updateQuickBuyImage(modal, html);
      modalElement._otfThumbColor = colorValueId;
      return;
    }
    // No resolved variant (a colour whose size is still unchosen, or a combination that has no variant): the section
    // falls back to the product's featured image, which may show another colour, so it is not applied.
    if (!colorValueId || modalElement._otfThumbColor === colorValueId) return;
    const currentImage = modal.find('.quickBuy--product-image');
    if (!currentImage.length) return;
    const selectedColor = modalElement.querySelector('.js-option-select[data-otf-color]')?.selectedOptions[0];
    if (selectedColor?.dataset.otfImage) {
      currentImage.attr('src', selectedColor.dataset.otfImage).attr('alt', selectedColor.dataset.otfImageAlt || '').css('visibility', '');
      modalElement._otfThumbColor = colorValueId;
      return;
    }
    // The product card's own swatch for this colour already carries that colour's image (it drives the card image swap).
    const card = modalElement.openedBy?.closest?.('[data-otf-card]');
    const swatch = card?.querySelector(`.radio-card-swatch[data-otf-color-id="${colorValueId}"]:not([data-product-url])`);
    let media = null;
    try { media = swatch?.getAttribute('data-variant-media') ? JSON.parse(swatch.getAttribute('data-variant-media')) : null; } catch (error) { media = null; }
    const src = media?.preview_image?.src;
    if (src) {
      currentImage.attr('src', `${src}${src.includes('?') ? '&' : '?'}width=300`).attr('alt', media.alt || '').css('visibility', '');
      modalElement._otfThumbColor = colorValueId;
      return;
    }
    // No image of the selected colour is known: the thumbnail is concealed in place (its box stays) until a variant resolves.
    currentImage.css('visibility', 'hidden');
    modalElement._otfThumbColor = null;
  }

  function updateQuickBuyButton(form, html) {
    var sourceButton = html.querySelector('.quick-add__submit[name="add"]');
    var currentButton = form.find('.quick-add__submit[name="add"]');

    if (!sourceButton || !currentButton.length) return;

    const needsSize = form.find('[data-otf-size]').toArray().some(select => !select.value);
    const variantInput = form.find('input[name="id"]')[0];
    const unavailable = variantInput?.disabled;
    const unavailableLabel = variantInput?.dataset.otfVariantMatches === 'false' ? window.variantStrings.unavailable : window.variantStrings.soldOut;
    currentButton.prop('disabled', needsSize || unavailable).removeAttr('aria-disabled');
    currentButton.find('.spn--add').removeClass('hidden').text(needsSize ? form[0].dataset.otfChooseSize : unavailable ? unavailableLabel : form[0].dataset.otfAddToBag || window.variantStrings.addToCart);
    currentButton.attr('aria-label', currentButton.find('.spn--add').text());
    currentButton.find('.sold-out-message').addClass('hidden');
  }

  function updateQuickBuyVariantInput(form, html, selectedValues) {
    var sourceInput = html.querySelector('input[name="id"]');
    var currentInput = form.find('input[name="id"]');

    if (!currentInput.length) return;

    if (sourceInput) {
      const needsSize = form.find('[data-otf-size]').toArray().some(select => !select.value);
      const variantId = sourceInput.dataset.otfVariantId || '';
      const variantOptions = JSON.parse(sourceInput.dataset.otfVariantOptions || '[]');
      const selects = form.find('.js-option-select').toArray();
      const chosenOptions = selects.map(select => select.value);
      const selectionRetained = selectedValues.size === selects.length && selects.every(select => selectedValues.get(select.dataset.index) === select.value);
      const matches = selectionRetained && Array.isArray(variantOptions) && variantOptions.length === chosenOptions.length && variantOptions.every((value, index) => value === chosenOptions[index]);
      currentInput[0].dataset.otfVariantMatches = String(matches);
      currentInput[0].dataset.otfVariantId = variantId;
      currentInput[0].dataset.otfVariantOptions = JSON.stringify(variantOptions);
      currentInput[0].dataset.otfAvailable = sourceInput.dataset.otfAvailable || 'false';
      currentInput[0].dataset.productUrl = sourceInput.dataset.productUrl || '';
      currentInput.val(needsSize || !matches ? '' : variantId);
      const quantity = html.querySelector('input[name="quantity"]');
      if (quantity) form.find('input[name="quantity"]').val(quantity.value);

      currentInput.prop('disabled', needsSize || !matches || !variantId || sourceInput.dataset.otfAvailable !== 'true');
      currentInput[0].dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.warn('Source input not found in server response');
    }
  }
}


